import React, { useMemo, useState } from 'react';
//HELPERS
import {
  LogOut,
  refreshUser,
  resetViewingAsAgent
} from 'state/actions/actions';
import { siteLoading, siteLoaded } from 'state/actions/site';
import { cn } from 'lib/utils';
//COMPONENTS
import MenuLink from 'tailwind-components/Sidebar/MenuLink';
//EXTERNAL
import {
  ChevronDown,
  Bell,
  Eye,
  EyeOff,
  ExternalLink,
  PlayCircle
} from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import companyNames from 'constants/companyNames';
import endpoints from 'constants/endpoints';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import useBrandLogo from 'hooks/useBrandLogo';
import {
  SET_POP_UP,
  SET_PUSH_NOTIFICATIONS_AND_POP_UPS
} from 'constants/types';
import dayjs from 'dayjs';
import { safePostCall } from 'helpers/firebaseSetup';
import OutsideAlerter from 'hooks/useClickOutside';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import ToggleSideBar from 'components/layout/ToggleSideBar/ToggleSideBar';
import useMobile from 'hooks/useMobile';
import useHiddenMode from 'hooks/useHiddenMode';
import useViewAs from 'hooks/useViewAs';
import { isAgentRankLimited } from 'helpers/agents';
import { getCompany, getFrontendInstance } from 'helpers/getCompanyVariables';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';
import { getTourSectionIconComponent } from 'tailwind-components/PortalWalkthrough/tourSectionIcons';
import icons from 'constants/icons';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';

const demoMenuBtnClass =
  'cursor-pointer flex w-full items-center gap-2 rounded-md border-0 bg-transparent px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50';

function DemoTourPickerItem({ Icon, children, onClick }) {
  return (
    <button type='button' className={demoMenuBtnClass} onClick={onClick}>
      <Icon size={16} className='shrink-0 text-grey-light' aria-hidden />
      {children}
    </button>
  );
}

DemoTourPickerItem.propTypes = {
  Icon: propTypes.elementType.isRequired,
  children: propTypes.node.isRequired,
  onClick: propTypes.func.isRequired
};

const company = getCompany();

const isOneill = company === 'oneill';
const isSunshine = company === 'sunshine';

// Hidden Mode is only available for O'Neill outside production.
const isHiddenModeAvailable =
  getFrontendInstance() !== 'production' && isOneill;

function PageHeader({ fullHeader }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [loadingNotificationId, setLoadingNotificationId] = useState(null);
  const [loadingAllNotifications, setLoadingAllNotifications] = useState(false);

  const isMobile = useMobile();
  const { hidden, toggleHidden } = useHiddenMode();
  // an agency that brought its own logo runs the portal under its own brand, so the
  // company's title steps aside. oneill keeps it: isAgencyLogo is never true there
  const { isAgencyLogo } = useBrandLogo();

  // "Sunshine Life and Health Advisors Portal" is too long for the mobile header,
  // where it pushed the notifications bell and the account menu off screen
  const portalTitle =
    isSunshine && isMobile
      ? `${companyNames.short} Portal`
      : `${companyNames.fullName} Portal`;

  const pushNotificationsAndPopUps = useSelector(
    state => state.notifications.push_notifications_and_pop_ups
  );
  const user = useSelector(state => state.user.zohoData);
  const { isViewingAs } = useViewAs();

  const isAgentLimited = isAgentRankLimited(user.Rank);

  const { startTour, sections, isTourDisabled } = usePortalWalkthrough() || {};
  const { pathname } = useLocation();
  const [demoPickerOpen, setDemoPickerOpen] = useState(false);

  const isOnHomeDashboard =
    pathname === '/home' || pathname.startsWith('/home?');

  const routeMatchedSections = useMemo(
    () =>
      (sections || []).filter(s => {
        const route = s.getRoute ? s.getRoute(user) : s.route;
        if (!route) return false;
        return pathname.startsWith(route.split('?')[0]);
      }),
    [sections, pathname, user]
  );
  // matchesPage() reads the DOM so it must run every render — not inside useMemo
  const currentPageSection =
    routeMatchedSections.find(s => s.matchesPage?.()) ||
    routeMatchedSections[0] ||
    null;

  const showCurrentPageTourOption =
    currentPageSection &&
    currentPageSection.id !== 'home' &&
    currentPageSection.id !== 'checklist';

  const handleShowDemoClick = () => {
    if (isOnHomeDashboard) {
      setDemoPickerOpen(false);
      startTour('home');
      return;
    }
    setDemoPickerOpen(open => !open);
  };

  const someUnreadNotifications = useMemo(() => {
    return (pushNotificationsAndPopUps || []).some(
      notif => notif.type === 'notification' && !notif.read_on_web
    );
  }, [pushNotificationsAndPopUps]);

  const handleExitViewAsAgentMode = () => {
    dispatch(siteLoading());
    dispatch(resetViewingAsAgent());
    navigate('/home', { replace: true });
    dispatch(
      refreshUser(() => {
        dispatch(siteLoaded());
      }, {})
    );
  };

  const handleOpenPopUp = popUp => {
    setNotificationsOpen(false);
    dispatch({
      type: SET_POP_UP,
      payload: popUp
    });
  };

  const notificationsByDate = useMemo(() => {
    const today = dayjs();
    const yesterday = dayjs().subtract(1, 'day');

    const todayNotifs = [];
    const yesterdayNotifs = [];
    const otherNotifs = [];

    (pushNotificationsAndPopUps || []).forEach(notif => {
      const notifDate = dayjs(notif.date);
      if (notifDate.isValid() && notifDate.isSame(today, 'day')) {
        todayNotifs.push(notif);
      } else if (notifDate.isValid() && notifDate.isSame(yesterday, 'day')) {
        yesterdayNotifs.push(notif);
      } else {
        otherNotifs.push(notif);
      }
    });

    return {
      today: todayNotifs,
      yesterday: yesterdayNotifs,
      other: otherNotifs
    };
  }, [pushNotificationsAndPopUps]);

  async function markNotificationAsRead(
    notificationIds = [],
    readOnWeb = true,
    readOnMobile = false
  ) {
    try {
      const res = await safePostCall(endpoints.awsProxy.safePost, {
        endpoint: endpoints.zoho.updatePushNotificationStatus,
        isZohoRequest: true,
        notificationIds: notificationIds,
        isMobile: readOnMobile,
        isWeb: readOnWeb
      });
      return !!res;
    } catch (e) {
      return false;
    }
  }

  const readNotification = async notificationIds => {
    if (notificationIds.length === 1) {
      setLoadingNotificationId(notificationIds[0]);
    }

    const success = await markNotificationAsRead(notificationIds, true, false);

    if (success) {
      const newNotifications = (pushNotificationsAndPopUps || []).map(
        notif => ({
          ...notif,
          ...(notificationIds.includes(notif.id) ? { read_on_web: true } : {})
        })
      );
      dispatch({
        type: SET_PUSH_NOTIFICATIONS_AND_POP_UPS,
        payload: newNotifications
      });
    }

    setLoadingNotificationId(null);
  };

  const markAllAsRead = async () => {
    const notificationIds = (pushNotificationsAndPopUps || [])
      .filter(notif => notif.type === 'notification')
      .map(notif => notif.id);
    setLoadingAllNotifications(true);

    await readNotification(notificationIds);

    setLoadingAllNotifications(false);
  };

  const markAsRead = notificationId => readNotification([notificationId]);

  const dropdownLinkClass =
    'py-2.5 px-3 hover:bg-grey-lightest transition-colors duration-150';

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 z-20 grow mb-[5px]',
        fullHeader && 'w-full left-0 right-auto',
        dropdown && 'z-2'
      )}
    >
      <div
        className={cn(
          'min-w-0 leading-[1.3] whitespace-nowrap flex items-center gap-2.5',
          'max-md:leading-none',
          '[&_span]:text-grey',
          '[&_span:first-child]:text-black [&_span:first-child]:text-[calc(var(--font-size-body)+2px)] [&_span:first-child]:font-medium',
          'max-md:[&_span]:text-(length:--font-size-note)',
          'max-md:[&_span:first-child]:text-[calc(var(--font-size-body)-2px)]'
        )}
      >
        <div className='block shrink-0 [&>div]:bg-[#f5f5f5] [&_svg]:w-[26px] [&_svg]:h-[26px] md:hidden'>
          <ToggleSideBar />
        </div>
        <div className='min-w-0'>
          {isViewingAs && (
            <span className='flex items-center gap-2 text-secondary-darkened! [&_svg]:w-[22px] [&_svg]:h-[22px]'>
              <Eye size={22} /> {'Viewing as agent'}
            </span>
          )}
          {!isViewingAs && !isAgencyLogo && (
            <span className={cn('block', isSunshine && 'truncate')}>
              {portalTitle}
            </span>
          )}

          {isViewingAs ? (
            <span className='max-[576px]:block max-[576px]:w-[180px] max-[576px]:truncate max-[576px]:overflow-hidden'>
              {`Agent: ${user.First_Name} ${user.Last_Name}`}
            </span>
          ) : (
            <span className={cn('block', isSunshine && 'truncate')}>
              Hi {user.First_Name}, welcome back!
            </span>
          )}
        </div>
      </div>
      {isViewingAs ? (
        <Button
          text='Leave'
          size='xsmall'
          variant='danger'
          outlined
          icon={<EyeOff size={20} />}
          onClick={handleExitViewAsAgentMode}
        />
      ) : (
        <div className='flex shrink-0 items-center gap-2.5'>
          {isOneill && startTour && !isTourDisabled && currentPageSection && (
            <div className='relative'>
              <Button
                variant='primaryDarkened'
                icon={<PlayCircle size={20} />}
                text='Show Demo'
                size='small'
                className='py-1!'
                onClick={handleShowDemoClick}
              />
              {demoPickerOpen && !isOnHomeDashboard && (
                <OutsideAlerter onClick={() => setDemoPickerOpen(false)}>
                  <div className='absolute right-0 top-[calc(100%+6px)] z-1100 flex min-w-[210px] flex-col gap-1 rounded-lg border border-[#e8e8e8] bg-white p-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)]'>
                    <DemoTourPickerItem
                      Icon={icons.home}
                      onClick={() => {
                        setDemoPickerOpen(false);
                        startTour();
                      }}
                    >
                      Start from Home
                    </DemoTourPickerItem>
                    {showCurrentPageTourOption && (
                      <DemoTourPickerItem
                        Icon={getTourSectionIconComponent(
                          currentPageSection.id
                        )}
                        onClick={() => {
                          setDemoPickerOpen(false);
                          startTour(currentPageSection.id);
                        }}
                      >
                        {`Start from ${currentPageSection.label}`}
                      </DemoTourPickerItem>
                    )}
                  </div>
                </OutsideAlerter>
              )}
            </div>
          )}
          {isHiddenModeAvailable && (
            <Button
              variant={hidden ? 'primaryLight' : 'primaryDarkened'}
              icon={hidden ? <EyeOff size={20} /> : <Eye size={20} />}
              aria-pressed={hidden}
              aria-label={
                hidden
                  ? 'Show sensitive information'
                  : 'Hide sensitive information'
              }
              title={
                hidden
                  ? 'Show sensitive information'
                  : 'Hide sensitive information'
              }
              onClick={toggleHidden}
            />
          )}
          <div data-tour='notifications-bell' className='relative'>
            {!isAgentLimited && (
              <Button
                variant={notificationsOpen ? 'primaryLight' : 'primaryDarkened'}
                icon={
                  <>
                    <Bell size={20} fill='currentColor' />
                    {someUnreadNotifications && (
                      <div className='absolute top-[18%] left-[45%] bg-red-500 text-white rounded-full w-2.5 h-2.5' />
                    )}
                  </>
                }
                onClick={e => {
                  e.stopPropagation();
                  setNotificationsOpen(prev => !prev);
                }}
              />
            )}
            {notificationsOpen && (
              <OutsideAlerter onClick={() => setNotificationsOpen(false)}>
                <div
                  data-tour='notifications-panel'
                  className={cn(
                    'absolute right-0 top-[calc(100%+8px)] w-[320px] max-h-[60vh] overflow-y-auto',
                    'bg-white border border-[#e8e8e8] rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-1000',
                    isMobile && 'right-[-110px]'
                  )}
                >
                  <div className='flex justify-between items-center py-2.5 px-3.5 border-b border-[#e8e8e8]'>
                    <div className='text-lg font-semibold text-[#222]'>
                      Notifications
                    </div>

                    {someUnreadNotifications &&
                      (loadingAllNotifications ? (
                        <div className='w-4 h-4 border-2 border-[#e0e0e0] border-t-primary rounded-full animate-[spin_0.8s_linear_infinite]' />
                      ) : (
                        <button
                          type='button'
                          onClick={markAllAsRead}
                          className='bg-transparent border-none text-primary cursor-pointer font-medium'
                        >
                          Mark all as read
                        </button>
                      ))}
                  </div>

                  {(pushNotificationsAndPopUps || []).length > 0 ? (
                    <>
                      {notificationsByDate.today.length > 0 && (
                        <>
                          <div className='text-base w-full py-2 px-3.5 text-[#222] font-semibold'>
                            Today
                          </div>
                          <RenderNotificationsAndPopUps
                            loadingAllNotifications={loadingAllNotifications}
                            notificationsAndPopUps={notificationsByDate.today}
                            markAsRead={markAsRead}
                            loadingNotificationId={loadingNotificationId}
                            handleOpenPopUp={handleOpenPopUp}
                          />
                        </>
                      )}
                      {notificationsByDate.yesterday.length > 0 && (
                        <>
                          <div className='text-base w-full py-2 px-3.5 text-[#222] font-semibold'>
                            Yesterday
                          </div>
                          <RenderNotificationsAndPopUps
                            loadingAllNotifications={loadingAllNotifications}
                            notificationsAndPopUps={
                              notificationsByDate.yesterday
                            }
                            markAsRead={markAsRead}
                            loadingNotificationId={loadingNotificationId}
                            handleOpenPopUp={handleOpenPopUp}
                          />
                        </>
                      )}
                      {notificationsByDate.other.length > 0 && (
                        <>
                          <div className='text-base w-full py-2 px-3.5 text-[#222] font-semibold'>
                            Past Notifications
                          </div>
                          <RenderNotificationsAndPopUps
                            loadingAllNotifications={loadingAllNotifications}
                            notificationsAndPopUps={notificationsByDate.other}
                            markAsRead={markAsRead}
                            loadingNotificationId={loadingNotificationId}
                            handleOpenPopUp={handleOpenPopUp}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <div className='py-3 px-3.5 text-[#555] text-center'>
                      No new notifications
                    </div>
                  )}
                </div>
              </OutsideAlerter>
            )}
          </div>
          <div
            data-tour='page-header-menu'
            className={cn(
              'h-full w-fit ml-auto p-1.5 rounded-t-[10px] relative cursor-pointer flex justify-center items-center gap-2',
              'before:content-[""] before:absolute before:bg-white before:h-5 before:w-full before:-bottom-[19px] before:left-0 before:opacity-0',
              dropdown &&
                'bg-white shadow-[0px_0px_20px_-10px_rgba(0,0,0,0.35)] before:opacity-100'
            )}
            onClick={e => {
              e.stopPropagation();
              setDropdown(prev => !prev);
            }}
          >
            <BrandLogo
              src={user.agentPhoto}
              alt=''
              className={cn(
                'h-10 w-10 rounded-full block mx-auto ring-1 ring-black/30 max-md:h-[35px] max-md:w-[35px]',
                // oneill's fallback logo is cropped to fill the circle, as it was before BrandLogo
                isOneill && 'object-cover'
              )}
            />
            <div
              className={cn(
                'flex min-w-0 flex-col justify-center leading-[1.2]',
                // only sunshine truncates on mobile; oneill keeps the full name and role
                isSunshine && 'max-md:max-w-[110px]'
              )}
            >
              <span
                className={cn(
                  'font-semibold select-none text-(length:--font-size-body) max-md:text-(length:--font-size-note)',
                  isSunshine && 'truncate'
                )}
              >
                {user.First_Name}
              </span>
              <span
                className={cn(
                  'text-[13px] font-medium text-grey-light max-md:text-[10px]',
                  isSunshine && 'truncate'
                )}
              >
                {user.Position_at_Agency ? user.Position_at_Agency : 'Agent'}
              </span>
            </div>
            <ChevronDown size={20} />
            <OutsideAlerter onClick={() => setDropdown(false)}>
              <div
                data-tour='profile-dropdown'
                className={cn(
                  'bg-white z-80 absolute -bottom-1.5 translate-y-full right-0 w-[220px] overflow-hidden rounded-lg',
                  'shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-[#ebebeb]',
                  'divide-y divide-[#ebebeb]',
                  dropdown ? 'block opacity-100' : 'hidden opacity-0'
                )}
              >
                <MenuLink
                  icon='agentInfo'
                  text='My account'
                  className={dropdownLinkClass}
                  to='/my-profile'
                />
                <MenuLink
                  icon='resetPassword'
                  text='Change password'
                  className={dropdownLinkClass}
                  to='/change-password'
                />
                {isOneill && (
                  <MenuLink
                    icon='info'
                    text='Change 2FA Phone Number'
                    className={dropdownLinkClass}
                    to='/change-2fa-phone'
                  />
                )}
                <MenuLink
                  icon='logOut'
                  text='Logout'
                  className={dropdownLinkClass}
                  tabType='danger'
                  onClick={() => dispatch(LogOut())}
                />
              </div>
            </OutsideAlerter>
          </div>
        </div>
      )}
    </div>
  );
}

PageHeader.propTypes = {
  fullHeader: propTypes.bool
};

export default PageHeader;

function RenderNotificationsAndPopUps({
  notificationsAndPopUps,
  markAsRead,
  loadingNotificationId,
  loadingAllNotifications,
  handleOpenPopUp = () => {}
}) {
  if (!notificationsAndPopUps?.length) return null;

  return (
    <SimpleContainer className='gap-1.5 w-full mb-1.5'>
      {notificationsAndPopUps.map(notif => (
        <div
          key={notif.id}
          className={cn(
            'py-1 px-2.5 pl-3.5',
            notif.read_on_web ? 'bg-grey-lightest' : 'bg-white'
          )}
        >
          <div className='flex items-start justify-between gap-2'>
            <div className='min-w-0'>
              <div className='font-medium text-[#222] mb-1 text-base'>
                {notif.type === 'notification'
                  ? notif.title
                  : notif.content.title}
              </div>
              <div className='text-[#555] text-[15px] whitespace-pre-wrap wrap-break-word'>
                {notif.type === 'notification'
                  ? notif.description
                  : notif.content.subtitle}
              </div>
              {notif.type === 'notification' && notif.external_url && (
                <a
                  href={notif.external_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center gap-1 mt-1.5 text-primary text-sm no-underline font-medium hover:underline'
                >
                  <span className='pt-1'>View Details</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {notif.type === 'pop-up' && (
                <button
                  type='button'
                  onClick={() => handleOpenPopUp(notif)}
                  className='bg-transparent border-none text-primary cursor-pointer p-0 text-sm no-underline font-medium hover:underline'
                >
                  View Details
                </button>
              )}
            </div>
            {notif.type === 'notification' && !notif.read_on_web && (
              <>
                {loadingNotificationId === notif.id ? (
                  <div className='flex items-center justify-center min-w-5 min-h-5'>
                    <div className='w-4 h-4 border-2 border-[#e0e0e0] border-t-primary rounded-full animate-[spin_0.8s_linear_infinite]' />
                  </div>
                ) : (
                  <button
                    type='button'
                    disabled={loadingAllNotifications}
                    onClick={() => markAsRead(notif.id)}
                    className={cn(
                      'bg-transparent border-none cursor-pointer p-0 text-xs whitespace-nowrap',
                      loadingAllNotifications
                        ? 'text-[#999] cursor-not-allowed opacity-50'
                        : 'text-primary'
                    )}
                  >
                    Mark as read
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </SimpleContainer>
  );
}

RenderNotificationsAndPopUps.propTypes = {
  notificationsAndPopUps: propTypes.array,
  markAsRead: propTypes.func,
  loadingNotificationId: propTypes.string,
  loadingAllNotifications: propTypes.bool,
  handleOpenPopUp: propTypes.func
};
